import { useRecoilValue } from 'recoil';
import { SECOND } from 'time-constants';

import { alertsHeadlinesState } from '@store';

import { Severity } from '../constants/types/api/alertsHeadlinesType';

// Used by the current conditions screen.  Should show all local alerts.
export const useWeatherAlerts = (maxCount = Number.MAX_SAFE_INTEGER) => {
  const { data } = useRecoilValue(alertsHeadlinesState);

  return data.filter(alert => isValidAlert(alert)).slice(0, maxCount);
};

/**
 * Alerts for LDL Alert Headline product
 * LDL Alert Headline product plays only the top 3 alerts by “headlinePriority"
 */
export const useWeatherAlertsHeadlines = (maxCount = 3) => {
  const { data } = useRecoilValue(alertsHeadlinesState);

  return data
    .filter(({ crawlOnly }) => !crawlOnly)
    .filter(alert => isValidAlert(alert))
    .sort(sortByHeadlinePriority)
    .slice(0, maxCount);
};

/**
 * Severity types that can activate LDL Severe Crawl/Bulletin on Live page.
 */
const severeSeverities = new Set([Severity.Severe, Severity.Extreme]);

/**
 * Severe Crawl Alerts
 * 1. Bucket the alerts by "crawlCategory"
 * 2. Remove all except for the highest value of "crawlCategory".
 * 3. Sort the alerts by "crawlPriority", highest to lowest.
 * 4. Take the top 3 alerts.
 */
export const useWeatherAlertsCrawl = (isSevere = false) => {
  const maxCount = 3;
  const { data } = useRecoilValue(alertsHeadlinesState);
  let alerts = data.filter(
    ({ severityCode, expireTimeUTC }) =>
      isValidAlert({ expireTimeUTC }) && (severeSeverities.has(severityCode) || !isSevere),
  );
  const highestCrawlCategory = getHighestCrawlCategory(alerts);

  alerts = alerts
    .filter(({ crawlCategory }) => crawlCategory === highestCrawlCategory)
    .sort(sortByCrawlPriority)
    .slice(0, maxCount);
  return {
    alerts,
    highestCrawlLimitMinutes: getHighestCrawlLimitMinutes(alerts),
    highestCrawlMinutesOnOff: getHighestCrawlMinutesOnOff(alerts),
    highestExpireTimeUTC: getHighestExpireTimeUTC(alerts),
  };
};

export const includedAlertDescriptions = new Set([
  'Severe Thunderstorm Warning',
  'Tornado Warning',
  'Flash Flood Warning',
]);

// This hook will filter for just the alerts we want to show on the map page:
// Severe Thunderstore Warning, Tornado Warning, Flash Flood Warning.
export const useWeatherMapAlerts = (maxCount = 3, useOverlayAlerts) => {
  const { data, alertOverlays } = useRecoilValue(alertsHeadlinesState);
  const alerts = useOverlayAlerts ? alertOverlays : data;

  return [...alerts]
    .filter(alert => isValidAlert(alert))
    .filter(({ eventDescription }) => includedAlertDescriptions.has(eventDescription))
    .sort(sortByHeadlinePriority)
    .slice(0, maxCount);
};

const getHighestCrawlCategory = (alerts = []) => Math.max(...alerts.map(({ crawlCategory }) => crawlCategory ?? 0));

const getHighestCrawlMinutesOnOff = (alerts = []) =>
  Math.max(...alerts.map(({ crawlMinutesOnOff }) => crawlMinutesOnOff ?? 10));

const getHighestCrawlLimitMinutes = (alerts = []) =>
  Math.max(...alerts.map(({ crawlLimitMinutes }) => crawlLimitMinutes ?? 0));

const getHighestExpireTimeUTC = (alerts = []) => Math.max(...alerts.map(({ expireTimeUTC }) => expireTimeUTC ?? 0));

const sortByCrawlPriority = (a, b) => b.crawlPriority - a.crawlPriority;

const sortByHeadlinePriority = (a, b) => b.headlinePriority - a.headlinePriority;

const isValidAlert = ({ expireTimeUTC }) => expireTimeUTC * SECOND > Date.prototype.getCurrent().getTime();
